import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './StoryboardWidget.module.scss';
import { CustomButton } from '../../../../components/Button/Button';
import { useTranslation } from 'react-i18next';
import SelectOption from '../../../../components/SelectOption/SelectOption';
import HomeButton from '../HomeButton/HomeButton';
import { DebounceInput } from 'react-debounce-input';
import cx from 'classnames';
import HomePageSectionHeader from '../HomePageSectionHeader/HomePageSectionHeader';
import { toCamelCase } from '../../../../utils/stringHelper';
import { checkURLFormat, normalizeURL } from '../../../../utils/urlUtils';
import TypebotEmbed from '../TypebotEmbed/TypebotEmbed';
import configs from '../../../../../../conf';
import { useUserContext } from '../../../../context/UserContext';
import SearchComponent from '../../../../components/SearchComponent/SearchComponent';
import Icon from 'engage/components/Icon/Icon';
import VormatsAPI from '../../../../../common/apis/VormatsAPI';
import SettingsDropdown from './components/SettingsDropdown/SettingsDropdown';
import { ORIENTATIONS, LANGUAGES, PARTICIPANTS } from 'engage/constants/projectCreatorConsts';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import StoryboardSpinner from 'engage/features/Storyboard/components/StoryboardSpinner/StoryboardSpinner';
import { ToastNotification } from '../../../../components/Toast/Toast';

const config = configs[process.env.APP_CONF];

const MIN_REQUEST_LENGTH = 10;
const MAX_TOTAL_ATTACHMENTS = 3;

const StoryboardWidget = ({ className }) => {
  const LOADING_PHASES = {
    UPLOADING: 'uploading',
    GENERATING: 'generating'
  };

  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [data, setData] = useState([]);
  const language = useMemo(() => i18n.language.slice(0, 2), [i18n.language]);
  const { userCompanyId, user, isScriptAssistantAttachmentsEnabled } = useUserContext();

  const [searchValue, setSearchValue] = useState('');
  const [searchState, setSearchState] = useState(false);

  const [showTypebot, setShowTypebot] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState(null);

  const [selectedInputType, setSelectedInputType] = useState(null);
  const [links, setLinks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploadedExternalContext, setUploadedExternalContext] = useState([]);
  const [processingFailures, setProcessingFailures] = useState([]);
  const [pendingLink, setPendingLink] = useState({ url: '', title: '' });
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [touchedFields, setTouchedFields] = useState({ url: false });
  const [videoOrientation, setVideoOrientation] = useState(ORIENTATIONS.LANDSCAPE);
  const [selectedLanguage, setSelectedLanguage] = useState(language === 'nl' ? LANGUAGES.NEDERLANDS : LANGUAGES.ENGLISH);
  const [numberOfClips, setNumberOfClips] = useState(null);
  const [selectedParticipants, setSelectedParticipants] = useState(PARTICIPANTS.ME);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);

  const acceptedFileTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'text/markdown',
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp'
  ];

  const acceptedFileExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md', '.csv', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.gif', '.webp'];

  const mostPopularList = useMemo(() => {
    const list = [];
    const mostPopularNames = data?.mostPopularTypes?.map(item => item.name);
    data &&
      data?.useCases?.forEach(useCase => {
        const filteredFromCategory = useCase.types.filter(type => mostPopularNames.includes(type.name));
        if (filteredFromCategory.length) {
          list.push(...filteredFromCategory.map(type => ({ ...type, categoryColor: useCase.categoryColor })));
        }
      });
    return list;
  }, [data]);

  const getContentByLanguage = useCallback((data) => {
    return data[language] ?? data['en'];
  }, [language]);

  const options = useMemo(() => {
    const optionsList = [{ id: 'mostPopular', name: t('home_page.storyboard_widget.most_popular') }];
    if (data?.useCases) {
      const preparedCategories = data?.useCases?.map(useCase => ({
        id: useCase.category,
        name: getContentByLanguage(useCase.categoryTitle),
        categoryColor: useCase.categoryColor,
      }));
      preparedCategories && optionsList.push(...preparedCategories);
    }
    return optionsList;
  }, [data, language, t, getContentByLanguage]);

  const contentRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const linkUrlInputRef = useRef(null);
  const settingsButtonRef = useRef(null);

  const [currentCategoryId, setCurrentCategoryId] = useState(options[0]?.id);

  const currentCategory = useMemo(() => options.find(item => item.id == currentCategoryId), [options, currentCategoryId]);

  const [currentUseCase, setCurrentUseCase] = useState(null);
  const [showInput, setShowInput] = useState(true);
  const [customRequest, setCustomRequest] = useState('');

  const useCases = useMemo(() => {
    if (searchValue?.trim()?.length) {
      const filteredTypes = data?.useCases?.map(({ types, categoryColor }) => {
        return types?.filter(({ title }) => title[language]?.toLowerCase().includes(searchValue?.toLowerCase())).map(type => ({ ...type, categoryColor }));
      });
      return filteredTypes.flat();
    }
    if (currentCategoryId === 'mostPopular') {
      return mostPopularList;
    }
    return data?.useCases?.find(useCase => useCase.category === currentCategoryId)?.types;
  }, [data, currentCategoryId, mostPopularList, searchValue]);

  const totalAttachments = useMemo(() => links.length + documents.length, [links.length, documents.length]);

  const isNextAllowed = useMemo(() => currentUseCase || customRequest.length > MIN_REQUEST_LENGTH, [currentUseCase, customRequest]);

  const handleCustomRequestSelected = () => {
    setShowInput(true);
    setCurrentUseCase(null);
    inputRef.current.focus();
  };

  const handleSelectUseCase = useCase => {
    setShowInput(false);
    setCurrentUseCase(useCase);
  };

  const handleUpdateCustomRequest = e => {
    const value = e.target?.value || '';
    setCustomRequest(value);
  };

  const handleNextClicked = async () => {
    if (showTypebot) {
      setShowTypebot(false);
      return;
    }

    setUploadedExternalContext([]);
    setProcessingFailures([]);

    if ((documents.length > 0 || links.length > 0) && isScriptAssistantAttachmentsEnabled) {
      setLoadingPhase(LOADING_PHASES.UPLOADING);
      try {
        const files = documents.map(doc => doc.file);
        const urls = links.map(link => link.url);

        const contextFileIds = await VormatsAPI.processExternalContext(files, urls);

        if (contextFileIds.failures && contextFileIds.failures.length > 0) {
          setProcessingFailures(contextFileIds.failures);

          ToastNotification({
            message: t('home_page.storyboard_widget.attachment_processing_failures'),
            iconName: false,
            isMobileView: false,
            timeout: 6000
          });

          contextFileIds.failures.forEach(failure => {
            console.warn('Processing failed:', failure);
            Sentry.captureMessage(`Document/URL processing failed: ${JSON.stringify(failure)}`, 'warning');
          });

          setLoadingPhase(null);
          return;
        }

        if (contextFileIds.successes && contextFileIds.successes.length > 0) {
          try {
            const videoLanguage = selectedLanguage?.type || language;

            setLoadingPhase(LOADING_PHASES.GENERATING);

            const generatedStory = await VormatsAPI.generateVideo(customRequest, videoLanguage, contextFileIds.successes, videoOrientation.type, numberOfClips, selectedParticipants.type);

            if (generatedStory && generatedStory.id) {
              history.push(`/eg/storyboard/${generatedStory.id}`);
              return;
            }
          } catch (generateError) {
            console.error('Error generating video:', generateError);
            Sentry.captureException(generateError);

            ToastNotification({
              message: t('home_page.storyboard_widget.video_generation_failed'),
              iconName: false,
              isMobileView: false,
            });
            return;
          }
        } else {
          // No successful context files, show typebot
          setShowTypebot(true);
        }
      } catch (error) {
        console.error('Error uploading external context:', error);
        Sentry.captureException(error);

        ToastNotification({
          message: t('home_page.storyboard_widget.upload_error'),
          iconName: false,
          isMobileView: false,
        });
      } finally {
        setLoadingPhase(null);
      }
    } else {
      setShowTypebot(true);
    }
  };

  const handleSelectSection = useCallback(category => {
    setCurrentCategoryId(category?.id);
    setCurrentUseCase(null);
    setShowInput(true);
  }, []);

  const handleLinkButtonClick = () => {
    if (totalAttachments >= MAX_TOTAL_ATTACHMENTS) {
      return;
    }

    if (selectedInputType === 'link') {
      setShowLinkInput(true);
    } else {
      setSelectedInputType('link');
      setShowLinkInput(true);
    }
    setTimeout(() => {
      if (linkUrlInputRef.current) {
        linkUrlInputRef.current.focus();
      }
    }, 0);
  };

  const handleLinkUrlKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddLink();
    }
  };

  const handleAddLink = () => {
    if (totalAttachments >= MAX_TOTAL_ATTACHMENTS) {
      return;
    }

    const normalizedUrl = normalizeURL(pendingLink.url);
    if (pendingLink.url && checkURLFormat(normalizedUrl)) {
      const finalUrl = normalizedUrl.replace(/\/$/, '');

      if (!links.some(link => link.url === finalUrl)) {
        const newLink = {
          id: Date.now() + Math.random(),
          url: finalUrl,
          title: pendingLink.title || finalUrl,
          isValid: true
        };
        setLinks(prev => [...prev, newLink]);
      }
      setPendingLink({ url: '', title: '' });
      setTouchedFields({ url: false });
      setShowLinkInput(false);

      // Focus back to the main input field
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  };

  const handleRemoveLink = (linkId) => {
    setLinks(prev => prev.filter(link => link.id !== linkId));
  };

  const handleCancelLink = () => {
    setPendingLink({ url: '', title: '' });
    setTouchedFields({ url: false });
    setShowLinkInput(false);
  };

  const handleDocumentButtonClick = () => {
    if (totalAttachments >= MAX_TOTAL_ATTACHMENTS) {
      return;
    }

    setSelectedInputType('document');
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    if (!event.target || !event.target.files) return;

    const files = Array.from(event.target.files);
    const targetElement = event.target;

    const validFiles = files.filter(file => {
      const isValidMimeType = acceptedFileTypes.includes(file.type);

      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      const isValidExtension = acceptedFileExtensions.includes(fileExtension);

      return isValidMimeType || isValidExtension;
    });

    const newDocuments = validFiles
      .filter(file => !documents.some(doc => doc.name === file.name && doc.size === file.size && doc.type === file.type))
      .slice(0, MAX_TOTAL_ATTACHMENTS - totalAttachments) // Limit to remaining slots
      .map(file => ({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        size: file.size,
        type: file.type
      }));

    if (newDocuments.length > 0) {
      setDocuments(prev => [...prev, ...newDocuments]);
    }

    targetElement.value = '';

    // Focus back to the main input field
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleRemoveDocument = (docId) => {
    setDocuments(prev => prev.filter(doc => doc.id !== docId));
  };

  const handleOrientationChange = (orientationType) => {
    const selectedOrientation = Object.values(ORIENTATIONS).find(o => o.type === orientationType);
    if (selectedOrientation) {
      setVideoOrientation(selectedOrientation);
    }
  };

  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
  };

  const handleToggleSettings = () => {
    setShowSettingsDropdown(prev => !prev);
  };

  const handleCloseSettings = () => {
    setShowSettingsDropdown(false);
  };

  const handleNumberOfClipsChange = (clips) => {
    setNumberOfClips(clips);
  };

  const handleParticipantsChange = (participants) => {
    setSelectedParticipants(participants);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);

    const validFiles = files.filter(file => {
      const isValidMimeType = acceptedFileTypes.includes(file.type);
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      const isValidExtension = acceptedFileExtensions.includes(fileExtension);

      return isValidMimeType || isValidExtension;
    });

    const newDocuments = validFiles
      .filter(file => !documents.some(doc => doc.name === file.name && doc.size === file.size && doc.type === file.type))
      .slice(0, MAX_TOTAL_ATTACHMENTS - totalAttachments) // Limit to remaining slots
      .map(file => ({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        size: file.size,
        type: file.type
      }));

    if (newDocuments.length > 0) {
      setDocuments(prev => [...prev, ...newDocuments]);

      // Focus back to the main input field
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  };



  const isUrlValid = useMemo(() => {
    if (!pendingLink.url) {
      return false;
    }
    return checkURLFormat(normalizeURL(pendingLink.url));
  }, [pendingLink.url]);

  const shouldShowValidationError = useMemo(() => {
    return touchedFields.url && pendingLink.url && !checkURLFormat(normalizeURL(pendingLink.url));
  }, [touchedFields.url, pendingLink.url]);

  const isLinkFailed = useCallback((link) => {
    return processingFailures.some(failure => failure.url === link.url);
  }, [processingFailures]);

  const isDocumentFailed = useCallback((document) => {
    return processingFailures.some(failure => failure.fileName === document.name);
  }, [processingFailures]);

  const getInputPlaceholder = () => {
    return t('home_page.storyboard_widget.custom_request.placeholder');
  };

  useEffect(() => {
    const fetchJsonData = async () => {
      const timestamp = new Date().getTime();
      const resp = await fetch(`${config.typebotAssistantDbJsonUrl}?t=${timestamp}`);
      const responseData = await resp.json();
      setData(responseData);
    };

    fetchJsonData();

    return () => {
      setShowTypebot(false);
      setCustomRequest('');
    }
  }, []);

  useEffect(() => {
    setSelectedLanguage(language === 'nl' ? LANGUAGES.NEDERLANDS : LANGUAGES.ENGLISH);
  }, [language]);

  useEffect(() => {
    const assitantChatVariables = {
      idea_answer: customRequest,
      template_answer: currentUseCase?.name ?? null,
      request_language: selectedLanguage?.type || language,
      assistant_db_json_url: config.typebotAssistantDbJsonUrl,
      company_id: userCompanyId,
      user_name: user?.userFullName,
      uploaded_external_context: uploadedExternalContext,
      number_of_clips: numberOfClips,
      video_orientation: videoOrientation?.type || 'landscape'
    };

    sessionStorage.setItem('assistantChatVariables', JSON.stringify(assitantChatVariables));
  }, [customRequest, currentUseCase, selectedLanguage, language, links, documents, userCompanyId, user, isScriptAssistantAttachmentsEnabled, uploadedExternalContext, numberOfClips, videoOrientation]);

  return (
    <div className={cx(styles.wrapper, className)}>
      <div className={styles.header}>
        <HomePageSectionHeader
          icon="scriptGenerator"
          title={t('home_page.script_generator')}
          description={t('home_page.script_generator.description')}
        />
        <CustomButton
          className={styles.nextBtn}
          label={t(showTypebot ? 'general.cancel' : 'storyboard.editor.next')}
          variant="basic"
          disabled={!isNextAllowed}
          onClick={handleNextClicked}
        />
      </div>
      {showTypebot ? (
        <TypebotEmbed />
      ) : (
        <div className={styles.content}>
          <div ref={contentRef} className={styles.selectionContainer}>
            <SelectOption
              parentRef={contentRef}
              currentlySelected={currentCategory}
              options={options}
              onSelect={handleSelectSection}
              currentInputClassName={styles.templateInputSelector}
              inputWidthByContent={true}
              selectedIcon={'checkSelectOption'}
              className={cx(styles.selectOption, { [styles.hide]: searchState })}
            />
            <SearchComponent searchState={setSearchState} inputClassName={styles.inputSearchComponent} searchValue={searchValue} onChange={setSearchValue} clearButton={true} />
          </div>
          <div className={styles.useCaseOptionsList}>
            {useCases?.map((useCase, index) => (
              <HomeButton
                key={`${useCase.name}_${index}`}
                label={getContentByLanguage(useCase.title)}
                icon={toCamelCase(useCase?.style)}
                onClick={() => handleSelectUseCase(useCase)}
                color={useCase?.categoryColor ?? currentCategory?.categoryColor}
                info={getContentByLanguage(useCase.description)}
                className={cx(styles.useCaseButton, { [styles.selected]: currentUseCase?.name === useCase.name })}
                searchValue={searchValue.trim()}
              />
            ))}
            <HomeButton
              label={'home_page.storyboard_widget.no_template'}
              icon={'noTemplate'}
              onClick={handleCustomRequestSelected}
              color="rgba(208, 198, 221, 1)"
              className={cx(styles.customRequestButton, { [styles.selected]: showInput })}
              keepIconColor={true}
            />
          </div>

          <div
            className={cx(styles.inputWrapper, {
              [styles.show]: showInput,
              [styles.dragOver]: dragOver
            })}
            onDragOver={isScriptAssistantAttachmentsEnabled ? handleDragOver : undefined}
            onDragLeave={isScriptAssistantAttachmentsEnabled ? handleDragLeave : undefined}
            onDrop={isScriptAssistantAttachmentsEnabled ? handleDrop : undefined}
          >
            <DebounceInput
              inputRef={inputRef}
              debounceTimeout={300}
              type="text"
              element={'textarea'}
              value={customRequest}
              onChange={handleUpdateCustomRequest}
              className={styles.inputContainer}
              placeholder={getInputPlaceholder()}
              tabIndex={0}
              aria-label={t('accessibility.type_brief')}
            />

            {isScriptAssistantAttachmentsEnabled && (
              <>
                {links.length > 0 && (
                  <div className={styles.attachmentsContainer}>
                    {links.map((link) => (
                      <div key={link.id} className={cx(styles.linkItem, { [styles.failedItem]: isLinkFailed(link) })}>
                        <Icon src="addLink" verticalAlign="middle" className={cx(styles.linkIcon)} />
                        <span className={styles.linkTitle}>{link.title || link.url}</span>
                        <button
                          className={styles.removeButton}
                          onClick={() => handleRemoveLink(link.id)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {documents.length > 0 && (
                  <div className={styles.attachmentsContainer}>
                    {documents.map((doc) => (
                      <div key={doc.id} className={cx(styles.documentItem, { [styles.failedItem]: isDocumentFailed(doc) })}>
                        <Icon src="addNote" verticalAlign="middle" className={cx(styles.documentIcon)} />
                        <span className={styles.documentName}>{doc.name}</span>
                        <button
                          className={styles.removeButton}
                          onClick={() => handleRemoveDocument(doc.id)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {showLinkInput && (
                  <div className={styles.linkInputModal}>
                    <input
                      ref={linkUrlInputRef}
                      type="url"
                      placeholder={t('home_page.storyboard_widget.add_link_placeholder')}
                      value={pendingLink.url}
                      onChange={(e) => {
                        const value = e.target?.value || '';
                        setPendingLink(prev => ({ ...prev, url: value }));
                      }}
                      onKeyPress={handleLinkUrlKeyPress}
                      onBlur={() => setTouchedFields(prevState => ({ ...prevState, url: true }))}
                      className={cx(styles.linkUrlInput, { [styles.invalidInput]: shouldShowValidationError })}
                    />
                    <div className={styles.linkInputActions}>
                      <CustomButton
                        onClick={handleAddLink}
                        className={styles.addLinkButton}
                        variant="primary"
                        size="small"
                        disabled={!isUrlValid}
                      >
                        {t('general.add')}
                      </CustomButton>
                      <CustomButton
                        onClick={handleCancelLink}
                        variant="secondary"
                        size="small"
                      >
                        {t('general.cancel')}
                      </CustomButton>
                    </div>
                  </div>
                )}

                <div className={styles.buttonContainer}>
                  <div className={styles.actionButtonsGroup}>
                    <CustomButton
                      onClick={handleLinkButtonClick}
                      variant="secondary"
                      size="small"
                      className={styles.contextButton}
                      disabled={totalAttachments >= MAX_TOTAL_ATTACHMENTS}
                    >
                      <Icon src="addLink" verticalAlign="middle" className={cx(styles.contextButtonIcon)} />
                      <span>{t('home_page.storyboard_widget.add_link')}</span>
                    </CustomButton>

                    <CustomButton
                      onClick={handleDocumentButtonClick}
                      variant="secondary"
                      size="small"
                      className={styles.contextButton}
                      disabled={totalAttachments >= MAX_TOTAL_ATTACHMENTS}
                    >
                      <Icon src="addNote" verticalAlign="middle" className={cx(styles.contextButtonIcon)} />
                      <span>{t('home_page.storyboard_widget.add_document')}</span>
                    </CustomButton>

                    {totalAttachments > 0 && (
                      <span className={styles.attachmentLimitText}>
                        {t('home_page.storyboard_widget.attachment_limit_reached', { max_count: MAX_TOTAL_ATTACHMENTS })}
                      </span>
                    )}
                  </div>

                  <div className={styles.settingsGroup}>
                    {(documents.length > 0 || links.length > 0) && (
                      <>
                        <CustomButton
                          ref={settingsButtonRef}
                          onClick={handleToggleSettings}
                          variant="secondary"
                          size="small"
                          className={styles.settingsButton}
                        >
                          <Icon src="settings" verticalAlign="middle" className={cx(styles.settingsButtonIcon)} />
                          <span>{t('general.settings')}</span>
                        </CustomButton>
                        <SettingsDropdown
                          isOpen={showSettingsDropdown}
                          onClose={handleCloseSettings}
                          videoOrientation={videoOrientation}
                          onOrientationChange={handleOrientationChange}
                          selectedLanguage={selectedLanguage}
                          onLanguageChange={handleLanguageChange}
                          numberOfClips={numberOfClips}
                          onNumberOfClipsChange={handleNumberOfClipsChange}
                          selectedParticipants={selectedParticipants}
                          onParticipantsChange={handleParticipantsChange}
                          triggerRef={settingsButtonRef}
                        />
                      </>
                    )}
                    <CustomButton
                      className={styles.nextInputButton}
                      label={t('storyboard.editor.next')}
                      variant="basic"
                      hide={!isNextAllowed}
                      onClick={handleNextClicked}
                    />
                  </div>
                </div>

                {/* Hidden File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className={styles.hiddenFileInput}
                  accept={acceptedFileTypes.join(',')}
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loadingPhase && (
        <div className={styles.loadingOverlay}>
          <StoryboardSpinner
            text={loadingPhase === LOADING_PHASES.UPLOADING
              ? t('home_page.storyboard_widget.uploading_processing')
              : t('home_page.storyboard_widget.generating_video')
            }
            subtext={loadingPhase === LOADING_PHASES.UPLOADING
              ? t('home_page.storyboard_widget.uploading_subtext')
              : t('home_page.storyboard_widget.generating_subtext')
            }
          />
        </div>
      )}
    </div>
  );
};

export default StoryboardWidget;
